/**
 * TanStack Query hook for the agent inbox list.
 *
 * Wraps GET /api/v1/agent/tickets with the filter set the SPEC §9
 * filter bar exposes. The hook accepts the full filter shape and
 * forwards only set keys as query parameters.
 *
 * The backend default already excludes spam from the list; the caller
 * passes `status: "spam"` explicitly when the Spam pill is active.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import api from "@/lib/api";
import type {
  TicketPriority,
  TicketStatus,
  TicketSummary,
  TicketType,
} from "@/types/api";

export type AssigneeFilter = "mine" | "unassigned" | "all" | string;

/** Allowlisted sort-by values for the agent list endpoint. Mirrors
 * backend LIST_SORT_FIELDS in app/api/tickets_agent.py. The backend
 * accepts "updated_at" as an alias for "last_activity_at" for
 * back-compat with stale localStorage values. */
export type ListSortBy =
  | "ticket_number"
  | "status"
  | "priority"
  | "requester_name"
  | "subject"
  | "assignee_name"
  | "company_name"
  | "last_activity_at";

export type SortOrder = "asc" | "desc";

export interface UseAgentTicketsArgs {
  status?: TicketStatus;
  assignee?: AssigneeFilter;
  priority?: TicketPriority;
  type?: TicketType;
  q?: string;
  companyId?: string;
  brandId?: string;
  /** Tag NAME filter (2026-07-13). Exact, case-insensitive match
   * server-side; one tag at a time to mirror the single-select Tag
   * dropdown. A stale / unknown name safely returns zero rows. */
  tag?: string;
  sort_by?: ListSortBy;
  sort_order?: SortOrder;
  /** Show ONLY soft-deleted tickets. Mutually exclusive with the
   * default behavior (which hides deleted rows). The Tickets page's
   * "Deleted" status pill drives this. */
  deletedOnly?: boolean;
  /** Compound preset switch. Two legal values:
   *
   * - ``"open_or_unassigned"`` (2026-05-20): team work queue,
   *   ``status IN (open, reopened) AND (assignee IS NULL OR
   *   assignee IN tracked-team)``.
   * - ``"mine_or_unassigned"`` (2026-05-26): personal work queue,
   *   ``status IN (open, reopened) AND (assignee IS NULL OR
   *   assignee = me)``.
   *
   * When set, the backend ignores the single ``status`` param (the
   * preset carries its own multi-status clause) and adds its own
   * assignee arm. EVERY other filter - the ``assignee`` dropdown
   * included - still ANDs on top of the preset's clauses, so a
   * refinement narrows the queue rather than being dropped. */
  view?: "open_or_unassigned" | "mine_or_unassigned";
}

export function agentTicketsQueryKey(
  args: UseAgentTicketsArgs,
): readonly unknown[] {
  return [
    "agent",
    "tickets",
    "list",
    args.status ?? "default",
    args.assignee ?? "all",
    args.priority ?? "all",
    args.type ?? "all",
    args.q ?? "",
    args.companyId ?? "",
    args.brandId ?? "",
    args.tag ?? "",
    args.sort_by ?? "updated_at",
    args.sort_order ?? "desc",
    args.deletedOnly ? "deleted" : "live",
    args.view ?? "no-view",
  ] as const;
}

// Polling cadence for the live agent-inbox view (2026-05-21 follow-
// up). Matches the existing sidebar badge cadence
// (useUnassignedTicketCount, useAdminInboxCount) so the whole left
// rail + page refresh on the same heartbeat. ``refetchIntervalIn-
// Background`` defaults to false in TanStack Query v5, so polling
// silently pauses while the tab is backgrounded. ``refetchOnWindow-
// Focus`` covers the snap-back: returning from another tab gives an
// immediate refetch instead of waiting up to 30s for the next tick.
const AGENT_INBOX_POLL_MS = 30_000;

export function useAgentTickets(
  args: UseAgentTicketsArgs = {},
): UseQueryResult<TicketSummary[]> {
  return useQuery({
    queryKey: agentTicketsQueryKey(args),
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (args.status) params.status = args.status;
      if (args.assignee) params.assignee = args.assignee;
      if (args.priority) params.priority = args.priority;
      if (args.type) params.type = args.type;
      if (args.q && args.q.length >= 2) params.q = args.q;
      if (args.companyId) params.company_id = args.companyId;
      if (args.brandId) params.brand_id = args.brandId;
      if (args.tag) params.tag = args.tag;
      if (args.sort_by) params.sort_by = args.sort_by;
      if (args.sort_order) params.sort_order = args.sort_order;
      if (args.deletedOnly) params.deleted_only = "true";
      if (args.view) params.view = args.view;
      const response = await api.get<TicketSummary[]>("/agent/tickets", {
        params,
      });
      return response.data;
    },
    refetchInterval: AGENT_INBOX_POLL_MS,
    refetchOnWindowFocus: true,
  });
}
